import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {debounce} from 'ts-debounce';

import * as analytics from 'analytics/Analytics';
import DataConnector from 'connect/DataConnector';
import {
  DEFAULT_BACKGROUND_IMAGE_URL_1X,
  DEFAULT_BACKGROUND_IMAGE_URL_2X,
  getNewUserFlowViewImages,
  JOIN_ORGANIZATION_SCREEN,
  MOLBIO_BACKGROUND_IMAGE_URL_1X,
  MOLBIO_BACKGROUND_IMAGE_URL_2X,
  NewUserFlowScreen,
  NOTEBOOK_BACKGROUND_IMAGE_URL_1X,
  NOTEBOOK_BACKGROUND_IMAGE_URL_2X,
  SCREEN_TO_ANALYTICS_NAME,
  SIGNUP_FLOW_V2_ACADEMIC_ROLE_INFO_SCREEN,
  SIGNUP_FLOW_V2_AFFILIATION_SCREEN,
  SIGNUP_FLOW_V2_INDUSTRY_ROLE_INFO_SCREEN,
  SIGNUP_FLOW_V2_JOIN_ORGANIZATION_SCREEN,
  SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN,
} from 'onboarding/new-user-flow/NewUserFlowConstants';
import AcademicRoleInfoScreen from 'onboarding/new-user-flow/signup-flow-v2/AcademicRoleInfoScreen';
import AffiliationScreen from 'onboarding/new-user-flow/signup-flow-v2/AffiliationScreen';
import IndustryRoleInfoScreen from 'onboarding/new-user-flow/signup-flow-v2/IndustryRoleInfoScreen';
import JoinOrganizationScreen from 'onboarding/new-user-flow/signup-flow-v2/JoinOrganizationScreen';
import MolBioOrNotebookScreen from 'onboarding/new-user-flow/signup-flow-v2/MolBioOrNotebookScreen';
import JoinOrganizationPage from 'parties/organizations/JoinOrganizationPage';
import EntityStore from 'stores/EntityStore';
import {retinaQueryWrapper} from 'styles/Utilities';

// @ts-expect-error
import CallToActionScreen from './CallToActionScreen';
// @ts-expect-error
import EntryInfoScreen from './EntryInfoScreen';
// @ts-expect-error
import MolBioInfoScreen from './MolBioInfoScreen';
import * as NewUserFlowActions from './NewUserFlowActions';
import {
  CALL_TO_ACTION_SCREEN,
  ENTRY_INFO_SCREEN,
  MOL_BIO_INFO_SCREEN,
  ROLE_INFO_SCREEN,
  ROLE_INFO_SCREEN_WITHOUT_FLOW,
} from './NewUserFlowConstants';
// @ts-expect-error
import NewUserFlowStore from './NewUserFlowStore';
import RoleInfoScreen from './RoleInfoScreen';

class NewUserFlowViewConnector extends DataConnector<NewUserFlowViewProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [NewUserFlowStore];

  enhanceProps(): EnhancedProps {
    return {
      isLoading: this.newUserFlowStore().getIsLoading(),
      projectId: this.newUserFlowStore().getProjectId(),
    };
  }
}

enum BackgroundImage {
  DEFAULT = 'DEFAULT',
  NOTEBOOK = 'NOTEBOOK',
  MOLBIO = 'MOLBIO',
}

export interface NewUserFlowViewProps {
  currentScreen: NewUserFlowScreen;
}

interface EnhancedProps {
  isLoading: boolean;
  projectId?: string;
}

interface NewUserFlowViewState {
  entryName: string;
  projectName: string;
  backgroundImage: BackgroundImage;
}

class NewUserFlowView extends React.Component<NewUserFlowViewProps & EnhancedProps, NewUserFlowViewState> {
  setBackgroundImage: (backgroundImage: BackgroundImage) => void;

  constructor(props: NewUserFlowViewProps & EnhancedProps) {
    super(props);
    this.state = {
      entryName: '',
      projectName: '',
      backgroundImage: BackgroundImage.DEFAULT,
    };
    this.setBackgroundImage = debounce((backgroundImage: BackgroundImage) => {
      this.setState({backgroundImage});
    }, 400);
  }

  override componentDidMount(): void {
    // Preload images to avoid flicker
    const images = getNewUserFlowViewImages();
    images.forEach((imgUrl) => {
      const preloadLink = document.createElement('link');
      preloadLink.href = imgUrl;
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      document.head.appendChild(preloadLink);
    });
  }

  override componentDidUpdate(prevProps: NewUserFlowViewProps & EnhancedProps): void {
    if (prevProps.currentScreen !== this.props.currentScreen) {
      if (Object.keys(SCREEN_TO_ANALYTICS_NAME).includes(this.props.currentScreen)) {
        analytics.track('onboarding_page', 'viewed', {
          page: SCREEN_TO_ANALYTICS_NAME[this.props.currentScreen],
          eventLastModified: 'FY22_Q2b3',
        });
      }
    }
  }

  nameCustomProject = (): void => {
    NewUserFlowActions.nameCustomProject({
      projectName: this.state.projectName,
    });
  };

  nameCustomEntry = (): void => {
    NewUserFlowActions.nameCustomEntry({
      entryName: this.state.entryName,
      projectId: this.props.projectId,
    });
    analytics.track('new_user_flow', 'finished_notebook_flow');
  };

  handleSetNotebookBackground = (): void => {
    this.setBackgroundImage(BackgroundImage.NOTEBOOK);
  };

  handleSetMolBioBackground = (): void => {
    this.setBackgroundImage(BackgroundImage.MOLBIO);
  };

  handleSetDefaultBackground = (): void => {
    this.setBackgroundImage(BackgroundImage.DEFAULT);
  };

  override render(): JSX.Element {
    return (
      <>
        <>
          <div
            className={css(styles.bgImage, styles.bgDefault)}
            style={{
              display: this.state.backgroundImage === BackgroundImage.DEFAULT ? 'inherit' : 'none',
            }}
          />
          <div
            className={css(styles.bgImage, styles.bgNotebook)}
            style={{
              display: this.state.backgroundImage === BackgroundImage.NOTEBOOK ? 'inherit' : 'none',
            }}
          />
          <div
            className={css(styles.bgImage, styles.bgMolbio)}
            style={{
              display: this.state.backgroundImage === BackgroundImage.MOLBIO ? 'inherit' : 'none',
            }}
          />
        </>
        <div className="newUserFlow-container" style={{position: 'relative', top: '-100%'}}>
          {(() => {
            switch (this.props.currentScreen) {
              case JOIN_ORGANIZATION_SCREEN:
                return <JoinOrganizationPage />;
              case CALL_TO_ACTION_SCREEN:
                return <CallToActionScreen />;
              case ENTRY_INFO_SCREEN:
                return <EntryInfoScreen />;
              case MOL_BIO_INFO_SCREEN:
                return <MolBioInfoScreen />;
              case ROLE_INFO_SCREEN:
                return <RoleInfoScreen inGettingStartedFlow={true} isLoading={this.props.isLoading} />;
              case ROLE_INFO_SCREEN_WITHOUT_FLOW: {
                const {phoneNumber, teamName, title} = EntityStore.currentUser();
                return (
                  <RoleInfoScreen
                    initialPhoneNumber={phoneNumber}
                    initialTeamName={teamName}
                    initialTitle={title}
                    inGettingStartedFlow={false}
                    isLoading={this.props.isLoading}
                  />
                );
              }
              case SIGNUP_FLOW_V2_AFFILIATION_SCREEN:
                return <AffiliationScreen />;
              case SIGNUP_FLOW_V2_ACADEMIC_ROLE_INFO_SCREEN:
                return <AcademicRoleInfoScreen />;
              case SIGNUP_FLOW_V2_INDUSTRY_ROLE_INFO_SCREEN:
                return <IndustryRoleInfoScreen />;
              case SIGNUP_FLOW_V2_JOIN_ORGANIZATION_SCREEN:
                return <JoinOrganizationScreen />;
              case SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN:
                return (
                  <MolBioOrNotebookScreen
                    hoverOff={this.handleSetDefaultBackground}
                    hoverMolBio={this.handleSetMolBioBackground}
                    hoverNotebook={this.handleSetNotebookBackground}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      </>
    );
  }
}

export default NewUserFlowViewConnector.connect(NewUserFlowView);

const styles = StyleSheet.create({
  bgImage: {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    height: '100%',
  },
  bgDefault: {
    backgroundImage: `url(${DEFAULT_BACKGROUND_IMAGE_URL_1X})`,
    ...retinaQueryWrapper({backgroundImage: `url(${DEFAULT_BACKGROUND_IMAGE_URL_2X})`}),
  },
  bgNotebook: {
    backgroundImage: `url(${NOTEBOOK_BACKGROUND_IMAGE_URL_1X})`,
    ...retinaQueryWrapper({backgroundImage: `url(${NOTEBOOK_BACKGROUND_IMAGE_URL_2X})`}),
  },
  bgMolbio: {
    backgroundImage: `url(${MOLBIO_BACKGROUND_IMAGE_URL_1X})`,
    ...retinaQueryWrapper({backgroundImage: `url(${MOLBIO_BACKGROUND_IMAGE_URL_2X})`}),
  },
});
